import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Linkedin, Facebook, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OBJETS = [
  "Demande d'information générale",
  "Partenariat commercial",
  "Distribution Wayne",
  "Réseau de stations",
  "Presse & communication",
  "Autre",
];

export default function Contact() {
  const [form, setForm] = useState({
    nom: "",
    email: "",
    telephone: "",
    objet: OBJETS[0],
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
    if (!emailOk) {
      toast.error("Veuillez saisir une adresse email valide.");
      return;
    }
    if (form.nom.trim().length < 2 || form.message.trim().length < 10) {
      toast.error("Veuillez remplir correctement tous les champs.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message envoyé — nous vous répondrons rapidement.");
      setForm({ nom: "", email: "", telephone: "", objet: OBJETS[0], message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Une erreur est survenue. Veuillez réessayer.";
      toast.error(typeof msg === "string" ? msg : "Erreur d'envoi");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-white py-24 sm:py-28"
    >
      <div className="kip-container grid lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Form */}
        <div className="lg:col-span-7 reveal">
          <span className="kip-overline">Contact</span>
          <span className="kip-accent-bar mt-4" />
          <h2 className="font-display font-black text-[#1A2744] text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
            Discutons de{" "}
            <span className="text-[#E85D26]">votre projet énergétique.</span>
          </h2>
          <p className="mt-4 text-[#64748B] text-lg max-w-xl">
            Partenariat, information ou opportunité commerciale — notre équipe
            vous répond sous 48 heures.
          </p>

          <form onSubmit={submit} noValidate className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="sm:col-span-1">
              <label className="block text-xs font-bold uppercase tracking-[0.14em] text-[#1A2744] mb-2">
                Nom complet *
              </label>
              <input
                data-testid="contact-input-nom"
                required
                value={form.nom}
                onChange={update("nom")}
                type="text"
                placeholder="Votre nom"
                className="w-full bg-[#F5F6F8] border border-transparent p-4 text-[#334155] focus:outline-none focus:border-[#E85D26] focus:bg-white transition-all"
              />
            </div>
            <div className="sm:col-span-1">
              <label className="block text-xs font-bold uppercase tracking-[0.14em] text-[#1A2744] mb-2">
                Email *
              </label>
              <input
                data-testid="contact-input-email"
                required
                value={form.email}
                onChange={update("email")}
                type="email"
                placeholder="vous@entreprise.com"
                className="w-full bg-[#F5F6F8] border border-transparent p-4 text-[#334155] focus:outline-none focus:border-[#E85D26] focus:bg-white transition-all"
              />
            </div>
            <div className="sm:col-span-1">
              <label className="block text-xs font-bold uppercase tracking-[0.14em] text-[#1A2744] mb-2">
                Téléphone
              </label>
              <input
                data-testid="contact-input-tel"
                value={form.telephone}
                onChange={update("telephone")}
                type="tel"
                placeholder="+225 XX XX XX XX XX"
                className="w-full bg-[#F5F6F8] border border-transparent p-4 text-[#334155] focus:outline-none focus:border-[#E85D26] focus:bg-white transition-all"
              />
            </div>
            <div className="sm:col-span-1">
              <label className="block text-xs font-bold uppercase tracking-[0.14em] text-[#1A2744] mb-2">
                Objet *
              </label>
              <select
                data-testid="contact-input-objet"
                required
                value={form.objet}
                onChange={update("objet")}
                className="w-full bg-[#F5F6F8] border border-transparent p-4 text-[#334155] focus:outline-none focus:border-[#E85D26] focus:bg-white transition-all"
              >
                {OBJETS.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-bold uppercase tracking-[0.14em] text-[#1A2744] mb-2">
                Message *
              </label>
              <textarea
                data-testid="contact-input-message"
                required
                value={form.message}
                onChange={update("message")}
                rows={5}
                placeholder="Décrivez votre demande..."
                className="w-full bg-[#F5F6F8] border border-transparent p-4 text-[#334155] focus:outline-none focus:border-[#E85D26] focus:bg-white transition-all resize-none"
              />
            </div>
            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="btn-primary w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Send size={16} />
                {loading ? "Envoi en cours..." : "Envoyer votre message"}
              </button>
            </div>
          </form>
        </div>

        {/* Info */}
        <aside className="lg:col-span-5 reveal">
          <div className="bg-[#1A2744] text-white p-8 lg:p-10">
            <div className="text-[#E85D26] text-xs tracking-[0.24em] uppercase font-bold mb-2">
              Coordonnées
            </div>
            <h3 className="font-display font-black text-2xl tracking-tight mb-8">
              KIP Energy — Côte d'Ivoire
            </h3>

            <ul className="space-y-6">
              <li className="flex items-start gap-4" data-testid="contact-address">
                <MapPin className="text-[#E85D26] shrink-0 mt-1" size={20} />
                <div>
                  <div className="font-semibold">Siège social</div>
                  <div className="text-white/70 text-sm mt-1">
                    RCI — Abidjan, Cocody 2 Plateaux
                  </div>
                </div>
              </li>
              <li className="flex items-start gap-4" data-testid="contact-phone">
                <Phone className="text-[#E85D26] shrink-0 mt-1" size={20} />
                <div>
                  <div className="font-semibold">Téléphone</div>
                  <a
                    href="tel:+2252522010120"
                    className="text-white/70 text-sm mt-1 hover:text-[#E85D26] transition-colors block"
                  >
                    +225 25 22 01 01 20
                  </a>
                  <a
                    href="tel:+2250502998703"
                    className="text-white/70 text-sm hover:text-[#E85D26] transition-colors block"
                  >
                    +225 05 02 99 87 03
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4" data-testid="contact-email">
                <Mail className="text-[#E85D26] shrink-0 mt-1" size={20} />
                <div>
                  <div className="font-semibold">Email</div>
                  <a
                    href="mailto:contact@kipenergy.ci"
                    className="text-white/70 text-sm mt-1 hover:text-[#E85D26] transition-colors block"
                  >
                    contact@kipenergy.ci
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4" data-testid="contact-hours">
                <Clock className="text-[#E85D26] shrink-0 mt-1" size={20} />
                <div>
                  <div className="font-semibold">Horaires</div>
                  <div className="text-white/70 text-sm mt-1">
                    Lundi – Vendredi, 8h00 – 18h00
                  </div>
                </div>
              </li>
            </ul>

            <div className="mt-10 pt-8 border-t border-white/10">
              <div className="text-[10px] tracking-[0.24em] uppercase text-white/50 font-bold mb-4">
                Suivez-nous
              </div>
              <div className="flex gap-3">
                {[
                  {
                    icon: Linkedin,
                    href: "https://www.linkedin.com/company/wayne-cote-d-ivoire/",
                    label: "LinkedIn",
                  },
                  {
                    icon: Facebook,
                    href: "https://www.facebook.com/WayneCotedivoire/",
                    label: "Facebook",
                  },
                ].map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    data-testid={`social-${s.label.toLowerCase()}`}
                    className="w-11 h-11 flex items-center justify-center border border-white/20 hover:bg-[#E85D26] hover:border-[#E85D26] transition-all"
                  >
                    <s.icon size={18} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}
